/**
 * Runtime guards: map delivery contexts → notification preference categories
 * and load profiles for `isNotificationEnabled` checks.
 *
 * Used by Rules Engine in-app delivery and template email runtime.
 * The notification-email Medplum bot uses a kept-in-sync copy under
 * `medplum-server/src/bots/notification-email-pref-guard.ts` (transpile-only bundle).
 */

import type { MedplumClient } from '@medplum/core';
import type { Patient, Practitioner, Task } from '@medplum/fhirtypes';

import type { ProfileRoleCode } from '@unify/fhir-model/constants';
import { getPatientRole } from '@unify/fhir-model/resources/patient';
import { getRole } from '@unify/fhir-model/resources/practitioner';

import {
  type NotificationPreferenceCategory,
  isNotificationEnabled,
} from '@/lib/notificationPreferences';

function isProfileRoleCode(role: string): role is ProfileRoleCode {
  return (
    role === 'unify-admin' ||
    role === 'unify-assistant' ||
    role === 'practice-owner' ||
    role === 'practice-admin' ||
    role === 'practice-assistant' ||
    role === 'supervisor' ||
    role === 'provider' ||
    role === 'client'
  );
}

/** Map Rules Engine FHIR Communication category codes + legacy codes → preference categories. */
export function normalizeRulesEngineCategoryToPreferenceCategory(
  displayCode: string | undefined,
  eventKey: string | undefined,
): NotificationPreferenceCategory {
  const code = displayCode?.trim() || defaultPreferenceCategoryForEventKey(eventKey);

  if (code === 'episode-of-care') {
    return eventKey === 'episode-of-care-requested' ? 'episode-requests' : 'episode-transfers';
  }
  if (code === 'forms') return 'forms-submitted';
  if (code === 'manual' || code === 'general') return 'rules-engine';
  // Legacy stored Communication categories
  if (code === 'tasks') return 'tasks-all';
  if (code === 'episode-updates') return 'episode-transfers';
  if (code === 'documents') return 'forms-submitted';

  const allowed: readonly NotificationPreferenceCategory[] = [
    'messages',
    'tasks-all',
    'tasks-priority',
    'appointments',
    'intake',
    'episode-requests',
    'episode-transfers',
    'assessments',
    'forms-uploaded',
    'forms-submitted',
    'supervision',
    'supervision-notes-due',
    'billing',
    'claims',
    'payments',
    'rules-engine',
    'system',
  ];
  if ((allowed as readonly string[]).includes(code)) {
    return code as NotificationPreferenceCategory;
  }
  return 'rules-engine';
}

/** Default preference category when intent has no explicit category (by trigger event). */
export function defaultPreferenceCategoryForEventKey(eventKey: string | undefined): NotificationPreferenceCategory {
  switch (eventKey) {
    case 'client-created':
    case 'client-intake-completed':
      return 'intake';
    case 'episode-of-care-requested':
      return 'episode-requests';
    case 'episode-of-care-started':
      return 'episode-transfers';
    case 'manual-rule-run':
      return 'rules-engine';
    default:
      return 'rules-engine';
  }
}

/** Map template / subscription trigger resource type → preference category (email path). */
export function preferenceCategoryForTriggerResourceType(resourceType: string): NotificationPreferenceCategory {
  switch (resourceType) {
    case 'Patient':
      return 'intake';
    case 'Appointment':
      return 'appointments';
    case 'EpisodeOfCare':
      return 'episode-transfers';
    case 'ServiceRequest':
      return 'episode-requests';
    case 'QuestionnaireResponse':
      return 'forms-submitted';
    case 'DocumentReference':
      return 'forms-uploaded';
    case 'Task':
      return 'tasks-all';
    case 'Communication':
      return 'messages';
    case 'Invoice':
    case 'ChargeItem':
      return 'billing';
    case 'Claim':
    case 'ClaimResponse':
      return 'claims';
    case 'PaymentNotice':
    case 'PaymentReconciliation':
      return 'payments';
    default:
      return 'system';
  }
}

function roleFromProfile(resource: Patient | Practitioner): ProfileRoleCode {
  if (resource.resourceType === 'Patient') {
    const r = getPatientRole(resource);
    return isProfileRoleCode(r) ? r : 'client';
  }
  const r = getRole(resource);
  return isProfileRoleCode(r ?? '') ? (r as ProfileRoleCode) : 'provider';
}

/**
 * Load Practitioner or Patient for a `ResourceType/id` recipient reference.
 * RelatedPerson and other types return null (caller should default-allow or skip).
 */
export async function loadProfileForNotificationGuard(
  medplum: MedplumClient,
  resourceReference: string,
): Promise<{ profile: Patient | Practitioner; role: ProfileRoleCode } | null> {
  const [rt, id] = resourceReference.split('/');
  if (!rt || !id) return null;
  if (rt !== 'Patient' && rt !== 'Practitioner') return null;
  try {
    const profile = await medplum.readResource(rt as 'Patient', id);
    if (profile.resourceType !== 'Patient' && profile.resourceType !== 'Practitioner') return null;
    return {
      profile: profile as Patient | Practitioner,
      role: roleFromProfile(profile as Patient | Practitioner),
    };
  } catch {
    return null;
  }
}

function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

/**
 * Find a Patient or Practitioner whose primary email telecom matches `email`.
 * Returns the first match (Patient first).
 */
export async function findProfileByEmail(
  medplum: MedplumClient,
  email: string,
): Promise<{ profile: Patient | Practitioner; role: ProfileRoleCode } | null> {
  const e = normalizeEmail(email);
  if (!e) return null;

  try {
    const patients = await medplum.searchResources('Patient', { email: e, _count: '5' });
    const p0 = patients[0] as Patient | undefined;
    if (p0?.id) {
      return { profile: p0, role: roleFromProfile(p0) };
    }
  } catch {
    // ignore search errors
  }

  try {
    const practs = await medplum.searchResources('Practitioner', { email: e, _count: '5' });
    const pr0 = practs[0] as Practitioner | undefined;
    if (pr0?.id) {
      return { profile: pr0, role: roleFromProfile(pr0) };
    }
  } catch {
    // ignore
  }

  return null;
}

const PRIORITY_TASK_PRIORITIES = new Set(['stat', 'asap', 'urgent']);

/** Route task notifications to all-tasks vs overdue/priority-only preference buckets. */
export function resolveTaskNotificationCategory(task: Task): 'tasks-all' | 'tasks-priority' {
  const priority = task.priority ?? 'routine';
  if (PRIORITY_TASK_PRIORITIES.has(priority)) return 'tasks-priority';

  const due = task.restriction?.period?.end;
  if (due && task.status !== 'completed' && task.status !== 'cancelled') {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dueDate = new Date(due);
    dueDate.setHours(0, 0, 0, 0);
    if (dueDate < today) return 'tasks-priority';
  }

  return 'tasks-all';
}

export { isNotificationEnabled };
